import {KTCard, KTIcon} from '_metronic/helpers'
import {FC, useEffect, useRef, useState} from 'react'
import ReactPaginate from 'react-paginate'
import RequestTableRow from '../table/RequestTableRow'
import {Timestamp, doc, collection, onSnapshot} from '@firebase/firestore'
import {useAuth} from 'app/modules/auth'
import db from 'services/firebase'
import {ToastContainer} from 'react-toastify'
import {FilterDropdown} from '../table/FilterDropdown'
import {ExportRequests} from '../utils/ExportRequests'
import {showSuccessToast} from 'app/components/ShowToast'
import Button from 'app/components/Button'
import ExportButton from 'app/components/ExportButton'

type Props = {
  requests: RequestDataType[]
  resources: {value: string; label: string}[]
}

export type RequestDataType = {
  id: string
  firstname: string
  lastname: string
  email: string
  company: string
  resources: {id: string; title: string; documentURL: string}[]
  requestedDate: Timestamp
  status: string
  decisionMadeBy?: {
    id: string
    name: string
    date: Timestamp
  }
}

const RequestsTab: FC<Props> = ({requests, resources}) => {
  const {currentUser} = useAuth()

  const [searchTerm, setSearchTerm] = useState('')
  const [searchLength, setSearchLength] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState(false)
  const [filterMatch, setFilterMatch] = useState(false)
  const [filteredRequests, setFilteredRequests] = useState<RequestDataType[]>(requests)
  const [selectedPerPageNumber, setSelectedPerPageNumber] = useState('15')
  const [currentPage, setCurrentPage] = useState(0)
  const [allSelected, setAllSelected] = useState(false)
  const [checkedRequests, setCheckedRequests] = useState<string[]>([])
  const [activeRowId, setActiveRowId] = useState('')
  const [personnels, setPersonnels] = useState<any[]>([])
  const [showAllResources, setShowAllResources] = useState(false)

  const [selectedResource, setSelectedResource] = useState<{label: string; value: string} | null>(
    null
  )
  const [selectedStatus, setSelectedStatus] = useState<{label: string; value: string} | null>(null)
  const [decisionMadeBy, setDecisionMadeBy] = useState<{label: string; value: string} | null>(null)

  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false)
  const filterDropdownRef = useRef<HTMLDivElement>(null)

  const pageCount = Math.ceil(filteredRequests.length / parseInt(selectedPerPageNumber))
  const offset = currentPage * parseInt(selectedPerPageNumber)
  const paginatedData = filteredRequests.slice(offset, offset + parseInt(selectedPerPageNumber))

  useEffect(() => {
    setFilteredRequests(requests)
  }, [requests])

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) {
      return
    }
    const tenantRef = doc(db, 'tenants', tenantId)
    const employeesRef = collection(tenantRef, 'employees')

    const unsubscribe = onSnapshot(employeesRef, (snapshot) => {
      const employees = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          end_date: doc.data().end_date,
          plan: doc.data().plan,
          uid: doc.data().uid,
        }))
        .filter((data) => !data.end_date)
      setPersonnels(employees)
    })

    return unsubscribe
  }, [])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target as Node) &&
        isFilterDropdownOpen
      ) {
        setIsFilterDropdownOpen(false)
      }
    }

    if (isFilterDropdownOpen) {
      document.addEventListener('mousedown', handleOutsideClick)
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [isFilterDropdownOpen])

  useEffect(() => {
    // Delay the rendering of the empty state for 2 seconds
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 2000)
    return () => clearTimeout(timeoutId)
  }, [])

  const handlePageClick = ({selected}: {selected: number}) => {
    setCurrentPage(selected)
    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  const activeFilterCount = [selectedResource, selectedStatus, decisionMadeBy].filter(
    Boolean
  ).length

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked
    setAllSelected(isChecked)
    setCheckedRequests(isChecked ? filteredRequests.map((request) => request.id) : [])
  }

  const handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {checked, value} = e.target
    setCheckedRequests((prev) => {
      const newCheckRequests = checked ? [...prev, value] : prev?.filter((id) => id !== value)
      setAllSelected(newCheckRequests?.length === filteredRequests.length)
      return newCheckRequests
    })
  }

  const handleDeselectClick = () => {
    setCheckedRequests([])
    setAllSelected(false)
  }

  const handleResetFilter = () => {
    setSelectedResource(null)
    setSelectedStatus(null)
    setDecisionMadeBy(null)
    setFilteredRequests(requests)
    setIsFilterDropdownOpen(false)
  }

  const filterData = (arg?: string) => {
    let filteredData = requests.filter((request) => {
      let matchesResource = true
      let matchesStatus = true
      let matchesDecisionMadeBy = true
      let matchesSearchTerm = true
      const term = typeof arg === 'string' ? arg : searchTerm

      if (selectedResource) {
        const resources = request.resources
        matchesResource =
          resources && resources.some((resource) => resource.id === selectedResource.value)
      }

      if (selectedStatus) {
        matchesStatus = request.status.toLowerCase() === selectedStatus.value.toLowerCase()
      }

      if (decisionMadeBy) {
        matchesDecisionMadeBy = request?.decisionMadeBy
          ? request.decisionMadeBy.id === decisionMadeBy.value
          : false
      }

      if (term) {
        matchesSearchTerm =
          request.firstname.toLowerCase().includes(term.toLowerCase()) ||
          request.lastname.toLowerCase().includes(term.toLowerCase()) ||
          request.email.toLowerCase().includes(term.toLowerCase()) ||
          request.company.toLowerCase().includes(term.toLowerCase())
      }

      return matchesResource && matchesStatus && matchesDecisionMadeBy && matchesSearchTerm
    })

    setFilteredRequests(filteredData)
    if (typeof arg === 'string') {
      setSearchLength(filteredData.length === 0)
    } else {
      setFilterMatch(filteredData.length === 0)
      setIsFilterDropdownOpen(false)
    }
  }

  const handleExport = async () => {
    try {
      await ExportRequests(requests)

      showSuccessToast({
        message: 'Requests have been exported successfully',
      })
    } catch (error) {
      console.error('Error while exporting request excel file.')
    }
  }

  return (
    <>
      <KTCard>
        <div className='card-header border-0 pt-6' style={{gap: '8px'}}>
          <div className='card-title d-flex justify-content-between align-items-center'>
            <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
            <input
              type='text'
              data-kt-user-table-filter='search'
              className='form-control w-250px ps-14'
              placeholder='Search..'
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                filterData(e.target.value)
              }}
            />
          </div>

          <div className='card-toolbar'>
            {checkedRequests.length > 0 ? (
              <div className='ms-auto border-end pe-4 text-center' style={{color: '#A1A5B7'}}>
                <span>
                  <p className='pt-4 mb-0'>{checkedRequests.length} Selected</p>
                </span>
              </div>
            ) : (
              <>
                <FilterDropdown
                  isOpen={isFilterDropdownOpen}
                  activeFilterCount={activeFilterCount}
                  toggleDropdown={() => setIsFilterDropdownOpen((prev) => !prev)}
                  resources={resources}
                  selectedResource={selectedResource}
                  setSelectedResource={setSelectedResource}
                  selectedStatus={selectedStatus}
                  setSelectedStatus={setSelectedStatus}
                  decisionMadeBy={decisionMadeBy}
                  setDecisionMadeBy={setDecisionMadeBy}
                  personnels={personnels}
                  handleResetClick={handleResetFilter}
                  filterData={filterData}
                  dropdownRef={filterDropdownRef}
                />

                <ExportButton className='me-4' onClick={handleExport} />
              </>
            )}
          </div>
        </div>

        <div className='card-body pt-4'>
          <div className='table-responsive'>
            <table className='table table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr
                  className='fw-bold'
                  style={{color: '#5A5A5A', borderBottom: '1px dashed #CCCCCC'}}
                >
                  {/* <th className='w-25px'>
                    <div className='form-check form-check-sm form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={allSelected}
                        data-kt-check='true'
                        data-kt-check-target='.widget-9-check'
                        onChange={handleSelectAllChange}
                      />
                    </div>
                  </th> */}
                  <th className='min-w-250px'>REQUESTER</th>
                  <th className='min-w-100px'>REQUESTED DATE</th>
                  <th className='min-w-100px'>DOCUMENTS REQUESTED</th>
                  <th className='min-w-100px'>STATUS</th>
                  <th className='min-w-100px'>DECISION MADE BY</th>
                  <th className='min-w-50px'></th>
                </tr>
              </thead>

              <tbody>
                {paginatedData && filteredRequests.length > 0 ? (
                  paginatedData.map((request, index) => (
                    <RequestTableRow
                      key={request.id}
                      request={request}
                      index={index}
                      activeRowId={activeRowId}
                      allSelected={allSelected}
                      checkedRequests={checkedRequests}
                      handleCheckChange={handleCheckChange}
                      showAllResources={showAllResources}
                      setShowAllResources={setShowAllResources}
                    />
                  ))
                ) : (
                  <tr style={{height: '500px'}}>
                    <td colSpan={10} className='text-center'>
                      {searchLength ? (
                        <div className='m-20'>
                          <img src='/media/buttons/emptystate.svg' alt='' />
                          <div className='m-5 fs-5'>
                            <p style={{fontWeight: 'bold'}}>No requests found</p>
                          </div>
                          <p>
                            We couldn't find any requests that match your search. Maybe try a
                            different search!
                          </p>
                        </div>
                      ) : !showEmptyState ? (
                        <div
                          className='d-flex justify-content-center align-items-center text-center'
                          style={{height: '500px'}}
                        >
                          <div
                            style={{
                              backgroundColor: '#fff',
                              color: '#7e8299',
                              fontWeight: '500',
                              margin: '0',
                              width: 'auto',
                              padding: '1rem 2rem',
                              top: 'calc(50% - 2rem)',
                              left: 'calc(50% - 4rem)',
                              position: 'absolute',
                              textAlign: 'center',
                            }}
                          >
                            <div
                              className='spinner-border mb-2 '
                              role='status'
                              style={{color: '#0E9F6E'}}
                            >
                              <span className='visually-hidden'>Loading...</span>
                            </div>
                          </div>
                        </div>
                      ) : filterMatch ? (
                        <div className='m-20'>
                          <img src='/media/buttons/emptystate.svg' alt='' />
                          <div className='fs-5'>
                            <p style={{fontWeight: 'bold'}}>No filter results</p>
                          </div>
                          <p>Maybe try a different filter or reset filters</p>
                        </div>
                      ) : (
                        <div
                          className='d-flex justify-content-center align-items-center text-center'
                          style={{height: '100%'}}
                        >
                          <div className='m-20'>
                            <img src='/media/buttons/emptystate.svg' alt='' />
                            <div className='m-5 fs-5'>
                              <p style={{fontWeight: 'bold'}}>No requests added</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                {filterData.length === 1 && (
                  <tr>
                    <td colSpan={4}>
                      <div style={{height: '55px'}}></div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {filteredRequests?.length >= 15 && (
          <div className='d-flex justify-content-between'>
            <div className='col-sm-12 col-md-5 d-flex align-items-center justify-content-center justify-content-md-start dt-toolbar'>
              <div>
                <select
                  name='kt_permissions_table_length'
                  aria-controls='kt_permissions_table'
                  className='form-select mx-8'
                  id='dt-length-1'
                  value={selectedPerPageNumber}
                  onChange={(e) => setSelectedPerPageNumber(e.target.value)}
                >
                  <option value='50'>50</option>
                  <option value='100'>100</option>
                  <option value='150'>150</option>
                </select>
              </div>
            </div>

            <div className='d-flex justify-content-end my-10'>
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                breakLabel={'...'}
                breakClassName={'break-me'}
                pageCount={pageCount}
                marginPagesDisplayed={1}
                pageRangeDisplayed={1}
                onPageChange={handlePageClick}
                containerClassName={'pagination'}
                activeClassName={'active'}
              />
            </div>
          </div>
        )}
      </KTCard>

      <ToastContainer />
    </>
  )
}

export default RequestsTab
